import { useCallback, useEffect, useState } from 'react'
import { Order, OrderStatus } from '../types/order'

// A poll response snapshotted before a status mutation (in-flight request, server
// read staleness) overwrites the cache with the OLD status, snapping the card back to
// its previous lane. An override shields the UI from any such stale write for its full
// TTL.
//
// The TTL is the whole mechanism: overrides are pruned on expiry ONLY, never when the
// server first confirms the new status. A stale response can still land AFTER the first
// confirming one — pruning on confirmation is exactly the bug that shipped on 2026-07-03
// and had to be fixed again on 2026-07-08 (see tasks/lessons.md). While the server
// agrees with the override it is a harmless no-op.
//
// Accepted trade-off: a status change made from another screen on a locally-advanced
// order is masked for at most the TTL.
//
// The TTL is COUPLED TO THE CALLER'S POLL INTERVAL and must exceed it. The last possible
// stale writer is a post-mutation refetch that Apollo deduped onto an already-in-flight
// poll, caching a pre-mutation snapshot; what finally corrects that cache is the next
// poll issued strictly AFTER the mutation. Let the TTL lapse before that poll lands and
// the card snaps back to its old lane and stays there until the poll arrives. Callers
// pass `pollInterval + headroom`, never a bare constant.
const DEFAULT_TTL_MS = 15000

interface StatusOverride {
  status: OrderStatus
  since: string | null
  // Claim stamp (driver screens): claimDelivery/releaseDelivery change status and
  // delivered_by_user in ONE transaction, so a stale snapshot that lies about the
  // status lies about the claim too — shield both together or "my delivery" flickers
  // to unclaimed for the TTL. `undefined` = caller doesn't track claims (the board's
  // updateOrderStatus path); `null` = explicitly unclaimed (release).
  claimedBy?: { id: number; name: string } | null
  at: number
}

/**
 * Optimistic status shield for polling order boards.
 *
 * Call `recordOverride` with the mutation's result, then run polled orders through
 * `applyOverrides` before grouping them into lanes.
 *
 * @param ttlMs How long an override shields the UI. MUST exceed the caller's poll
 *              interval — see the note above.
 */
export function useStatusOverrides(ttlMs: number = DEFAULT_TTL_MS) {
  const [overrides, setOverrides] = useState<Record<number, StatusOverride>>({})

  const recordOverride = useCallback((orderId: number, order: Pick<Order, 'status' | 'current_status_since'> & Partial<Pick<Order, 'delivered_by_user'>>) => {
    setOverrides(prev => ({
      ...prev,
      [orderId]: {
        status: order.status,
        since: order.current_status_since ?? null,
        // Distinguish "not tracked" (key absent) from "unclaimed" (null).
        ...('delivered_by_user' in order ? { claimedBy: order.delivered_by_user ?? null } : {}),
        at: Date.now(),
      },
    }))
  }, [])

  // Prune expired entries on every render pass that changes the map, plus a timer armed
  // for the earliest expiry so overrides still lapse if polling stalls entirely.
  useEffect(() => {
    const prune = () =>
      setOverrides(prev => {
        const now = Date.now()
        const entries = Object.entries(prev).filter(([, o]) => now - o.at < ttlMs)
        return entries.length === Object.keys(prev).length ? prev : Object.fromEntries(entries)
      })
    prune()
    const expiries = Object.values(overrides).map(o => o.at + ttlMs - Date.now())
    if (expiries.length === 0) return
    const timer = setTimeout(prune, Math.max(Math.min(...expiries), 0) + 50)
    return () => clearTimeout(timer)
  }, [overrides, ttlMs])

  const applyOverrides = useCallback(
    (orders: Order[]): Order[] =>
      orders.map(o => {
        const override = overrides[o.id]
        if (override && Date.now() - override.at < ttlMs && o.status !== override.status) {
          return {
            ...o,
            status: override.status,
            current_status_since: override.since ?? o.current_status_since,
            // Only when the caller recorded a claim: a snapshot stale on status is
            // stale on the claim too (they change in one transaction server-side).
            ...(override.claimedBy !== undefined ? { delivered_by_user: override.claimedBy } : {}),
          }
        }
        return o
      }),
    [overrides, ttlMs]
  )

  return { applyOverrides, recordOverride, overrides }
}
